import React, { useState, useEffect, useCallback } from 'react';
import rewardsService from '../services/rewardsService';
import './RecompensaInfoModal.css';

const formatarValor = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarData = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

// Rótulos amigáveis para os tipos de lançamento do ledger da frota.
const LABELS = {
  accrual: 'Crédito por manutenção',
  wallet_bonus: 'Bônus de carteira',
  mission_reward: 'Recompensa de missão',
  wallet_conversion: 'Conversão de carteira',
  clawback: 'Estorno de crédito',
  expiry: 'Expiração',
  adjustment: 'Ajuste',
  redemption: 'Abate em fatura',
  cash_payout: 'Pagamento em espécie'
};

/**
 * Modal que explica o que é o saldo de recompensa e mostra o extrato (ledger).
 * O saldo vive na frota (programa de recompensas da manutenção); aqui é só leitura.
 */
function RecompensaInfoModal({ saldo, onClose }) {
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setErro(null);
      const data = await rewardsService.getExtrato();
      setEntries(data?.entries || []);
    } catch (e) {
      setErro('Não foi possível carregar o extrato agora.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div className="rec-modal-overlay" onClick={onClose}>
      <div className="rec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rec-modal-header">
          <h2>🎁 Crédito de Recompensa</h2>
          <button className="rec-modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        <div className="rec-modal-saldos">
          <div className="rec-saldo-box">
            <span className="rec-saldo-label">Disponível para abater</span>
            <span className="rec-saldo-valor rec-verde">{formatarValor(saldo?.disponivel)}</span>
          </div>
          <div className="rec-saldo-box">
            <span className="rec-saldo-label">Pendente (fecha no mês)</span>
            <span className="rec-saldo-valor">{formatarValor(saldo?.pendente)}</span>
          </div>
        </div>

        <div className="rec-modal-explica">
          <p><strong>O que é isso?</strong> Você acumula crédito no programa de recompensas do sistema de manutenção. Esse crédito <strong>abate a taxa das suas faturas</strong> — ao abater, a taxa cai e o valor que você recebe aumenta pelo mesmo montante.</p>
          <ul>
            <li><strong>Disponível</strong>: já liberado, pode ser usado para abater taxa agora.</li>
            <li><strong>Pendente</strong>: acumulado no mês corrente; vira disponível no fechamento mensal.</li>
          </ul>
          <p className="rec-modal-nota">O saldo é calculado e mantido pelo sistema de manutenção — aqui você consulta e usa.</p>
        </div>

        <div className="rec-modal-extrato">
          <h3>Extrato</h3>
          {loading ? (
            <div className="rec-modal-loading">Carregando extrato…</div>
          ) : erro ? (
            <div className="rec-modal-erro">{erro}</div>
          ) : entries.length === 0 ? (
            <div className="rec-modal-vazio">
              Nenhum lançamento ainda. Assim que houver crédito no programa de manutenção, os lançamentos aparecem aqui.
            </div>
          ) : (
            <div className="rec-extrato-lista">
              {entries.map((e) => {
                const credito = e.direction === 'credit';
                return (
                  <div className="rec-extrato-item" key={e.id}>
                    <div className="rec-extrato-info">
                      <span className="rec-extrato-tipo">{LABELS[e.entry_type] || e.entry_type}</span>
                      <span className="rec-extrato-data">{formatarData(e.occurred_at)}{e.invoice_ref ? ` · ${e.invoice_ref}` : ''}</span>
                    </div>
                    <span className={`rec-extrato-valor ${credito ? 'rec-verde' : 'rec-vermelho'}`}>
                      {credito ? '+' : '−'} {formatarValor(e.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RecompensaInfoModal;
